import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import C3Chart from 'react-c3js';
import { Card, CardTitle, CardText } from 'material-ui/Card';
import { List, ListItem } from 'material-ui/List';
import Divider from 'material-ui/Divider';
import FontIcon from 'material-ui/FontIcon';
import Avatar from 'material-ui/Avatar';
import RaisedButton from 'material-ui/RaisedButton';
import _ from 'lodash';

import { BottomNavigation, BottomNavigationItem } from 'material-ui/BottomNavigation';

import 'c3/c3.css';

import Loading from '../../Common/Loading';
import { fetchJob } from '../../../actions/jobs';
import { fetchEmployees } from '../../../actions/employees';
import { fetchRecentActivity } from '../../../actions/activity';
import Dashboard from '../../Dashboard';
import { toArray, formatLocation, sortStrings } from '../../../utils/helper';
import { fetchTasksRoutine } from '../../../routines';
import icons from '../../../utils/icons';

import styles from './styles';

const getAction = (activity) => {
  const stage = activity.stage ? activity.stage : { name: 'Task Stage', relativeTo: 'origin' };
  switch (activity.action) {
    case 'job-started' :
      return 'Job Started';
    case 'task-started' :
    case 'task-stage-started' :
      return `${stage.name} Started - ${activity.task ? formatLocation(activity.task[stage.relativeTo]) : ''}`;
    case 'task-stage-completed' :
      return `${stage.name} Completed - ${activity.task ? formatLocation(activity.task[stage.relativeTo]) : ''}`;
    case 'task-item-acknowledged':
      return `${stage.name} - ${activity.item ? activity.item.type.name : 'Item'} acknowledged - ${activity.task ? formatLocation(activity.task[stage.relativeTo]) : ''}`;
    default:
      return 'Unknown Activity';
  }
};

const generateProgress = (progress) => ({
  data: {
    columns: [
      ['data', progress],
    ],
    type: 'gauge',
  },
  gauge: {},
  color: {
    pattern: ['#FF0000', '#F97600', '#F6C600', '#60B044'], // the three color levels for the percentage values.
    threshold: {
      values: [30, 60, 90, 100],
    },
  },
  size: {
    height: 180,
  },
});

const generateStageProgress = (statistics) => {
  const progress = statistics ? (statistics.complete * 100) / statistics.total : 0;
  return generateProgress(progress);
};

// TODO use pre-calculated statistics
const generateJobProgress = (stats) => {
  const result = _.values(stats).reduce((res, stage) => {
    return {
      total: res.total + stage.total,
      complete: res.complete + stage.complete,
    };
  }, { total: 0, complete: 0 });

  return generateProgress((result.complete * 100) / result.total);
};

const generateStageBarChart = (data, max, categories) => ({
  data: {
    columns: data,
    type: 'bar',
  },
  axis: {
    x: {
      type: 'category',
      categories,
    },
    y: {
      max,
      min: 0,
      padding: { top: 0, bottom: 0 },
    },
  },
  bar: {
    width: {
      ratio: 0.5, // this makes bar width 50% of length between ticks
    },
  },
});

const getLastCompletion = (stage = {}) => {
  console.log('getLastComp', stage);
  return _.values(stage.completed || {}).reduce((res, completion) => {
    if (completion.at) {
      // console.log('completion.at', completion.at);
      if (!res || moment(res.at).isBefore(completion.at)) {
        // console.log('setting comp', completion);
        return completion;
      }
    }
    return res;
  }, null);
};

const generateTimeSeriesChart = (columns) => {
  return {
    data: {
      x: 'x',
      xFormat: '%Y-%m-%d %H:%M', // 'xFormat' can be used as custom format of 'x'
      columns,
    },
    axis: {
      x: {
        type: 'timeseries',
        tick: {
          // format: '%m/%d %H:%M',
          format: (x) => {
            return moment(x).format('MMM D, h:mm a');
          },
        },
      },
      y: {
        min: 0,
        padding: { bottom: 0 }
      },
    },
  };
};

const generateJobProgressTimeSeries = (tasks, stages = []) => {
  let scale = {};
  const data = tasks.reduce((res, task) => {
    toArray(task.stages || {}).forEach((taskStage) => {
      const completion = getLastCompletion(taskStage);
      if (completion) {
        console.log('completion.at', task, completion);
        const completions = res[taskStage.id] || [];
        completions.push(moment(completion.at));
        console.log('completion.a2t2');
        if (!scale.min || moment(completion.at).isBefore(scale.min)) {
          scale.min = moment(completion.at);
        }
        if (!scale.max || moment(completion.at).isAfter(scale.max)) {
          scale.max = moment(completion.at);
        }

        res[taskStage.id] = completions;
      }
    });
    return res;
  }, {});

  console.log('scale', scale);
  let start = (scale.min || moment()).startOf('h');
  let end = (scale.max || moment()).startOf('h').add(1, 'h');
  console.log('startend', start, end);

  const diff = (end.unix() - start.unix()) / 60;
  const steps = 30;

  let stepInterval = diff / steps;
  let stepUnit = 'minute';
  console.log('stepInterval-bef', stepInterval, diff, steps);

  if (stepInterval < 60) {
    stepInterval = 60 / Math.floor(60 / stepInterval);
  } else if (stepInterval < 1440) {
    stepInterval = stepInterval / 60;
    stepInterval = 24 / Math.floor(24 / stepInterval);
    start = start.startOf('day');
    end = end.startOf('day').add(1, 'day');
    stepUnit = 'hour';
  } else {
    stepInterval = 1;
    stepUnit = 'day';
    start = start.startOf(stepUnit);
    end = end.startOf(stepUnit).add(1, stepUnit);
  }

  const interval = start;
  const intervals = [];
  while (interval.isSameOrBefore(end)) {
    intervals.push(moment(interval).startOf(stepUnit));
    interval.add(stepInterval, stepUnit);
  }

  console.log('intervals', intervals.map(i => i.toISOString()));

  const data2 = _.mapValues(data, (times) => {
    console.log('times', times);
    return times.map((time) => {
      time.set(stepUnit, stepInterval * (Math.ceil(time.get(stepUnit) / stepInterval) + 0)).startOf(stepUnit);
      return time;
    }).reduce((res, time) => {
      res[time.toISOString()] = (res[time.toISOString()] || 0) + 1;
      return res;
    }, {});
  });

  console.log('data2', data2);

  const dateFormat = 'YYYY-MM-DD HH:mm';

  const cumulativeColumns = [_.flatten(['x', intervals.map((i) => i.format(dateFormat))])];
  stages.forEach((stage) => {
    let total = 0;
    cumulativeColumns.push(_.flatten([stage.name, intervals.map((i) => {
      // console.log('data2[stage.id]', data2[stage.id]);
      total += data2[stage.id] ? data2[stage.id][i.toISOString()] || 0 : 0;
      return total;
    })]));
  });
  cumulativeColumns.push(_.flatten(['Expected Tasks', _.range(intervals.length).map(() => tasks.length)]));
  console.log('cumulativeColumns', cumulativeColumns);

  const absoluteColumns = [_.flatten(['x', intervals.map((i) => i.format(dateFormat))])];
  stages.forEach((stage) => {
    absoluteColumns.push(_.flatten([stage.name, intervals.map((i) => data2[stage.id] ? data2[stage.id][i.toISOString()] || 0 : 0)]));
  });
  console.log('absoluteColumns', absoluteColumns);

  return {
    absolute: generateTimeSeriesChart(absoluteColumns),
    cumulative: generateTimeSeriesChart(cumulativeColumns),
  };
};

const calculateStatistics = (job, tasks) => {
  return ((job.profile || {}).stages || []).reduce((res, st) => {
    console.log('stage', st);
    const stageStats = {
      complete: (tasks || []).filter((task) => task.stages && task.stages[st.id] && task.stages[st.id].completed).length,
      total: (tasks || []).length,
    };
    res[st.id] = stageStats;
    return res;
  }, {});
};

class JobReport extends Component {
  static propTypes = {
    match: PropTypes.object.isRequired,
    fetchJob: PropTypes.func.isRequired,
    fetchTasks: PropTypes.func.isRequired,
    fetchEmployees: PropTypes.func.isRequired,
    fetchRecentActivity: PropTypes.func.isRequired,
    job: PropTypes.object.isRequired,
    tasks: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    dialogs: PropTypes.object.isRequired,
    activity: PropTypes.array.isRequired,
  };

  state = {
    selectedStageIndex: 0,
    gauge: true,
    statistics: {},
    cumulative: true,
    // jobProgressData: { data: {} },
    jobProgressTimeSeriesData: {
      // absolute: {
      //   data: {},
      // },
      // cumulative: {
      //   data: {},
      // },
    },
    processing: true,
  };

  componentWillMount() {
    console.log('this.match', this.props);
    const jobId = this.props.match.params.jobId;
    this.loadData(jobId);
  }

  componentWillReceiveProps(newProps) {
    if (this.props.match.params.jobId !== newProps.match.params.jobId) {
      this.loadData(newProps.match.params.jobId);
    } else {
      const { job, tasks, loading } = newProps;
      console.log('tasks',tasks);
      if (tasks && tasks.length > 0) {
        const statistics = calculateStatistics(newProps.job, newProps.tasks);
        const jobProgressData = generateJobProgress(statistics);
        const jobProgressTimeSeriesData = generateJobProgressTimeSeries(tasks, job.profile ? job.profile.stages : []);

        this.setState({
          processing: false,
          statistics,
          jobProgressData,
          jobProgressTimeSeriesData,
        });
      } else if (!loading && tasks && tasks.length === 0) {
        this.setState({
          processing: false,
        });
      }
    }
  }

  loadData(jobId) {
    this.props.fetchJob(jobId);
    this.props.fetchTasks(jobId);
    this.props.fetchEmployees();
    this.props.fetchRecentActivity(jobId, 5);
  }

  handleSelectStage = (stage, idx) => {
    console.log('handleSelectStage', stage, idx);
    this.setState({
      selectedStageIndex: idx,
    });
  };

  renderStageNavigationItem = (stage, idx) => (<BottomNavigationItem
    key={stage.id}
    label={stage.name}
    icon={<div style={{ textAliogn: 'center', }}><img alt="Stage" className="material-icons" src={icons[stage.icon]}
                                                      style={styles.stageIcon}/></div>}
    onClick={() => this.handleSelectStage(stage, idx)}

  />);

  renderStageProgressChart(stage) {
    console.log('renderStageProgressChart', stage);
    if (!stage) {
      return this.renderNoData();
    }

    const stageProgressChart = generateStageProgress(this.state.statistics[stage.id]);
    return (<C3Chart data={stageProgressChart.data} color={stageProgressChart.color} size={{ height: 250 }}/>);
  }

  renderStageBarChart() {
    const { job } = this.props;
    if (!job || !job.profile || !job.profile.stages) {
      return this.renderNoData();
    }

    const data = [];
    const total = [];
    const categories = [];
    const counts = ['Completed'];
    const stats = this.state.statistics;
    this.props.job.profile.stages.forEach((stage) => {
      categories.push(stage.name);
      counts.push(stats[stage.id] ? stats[stage.id].complete : 0);
      total.push(stats[stage.id] ? stats[stage.id].total : 0);
    });
    const max = Math.max(...total);
    data.push(counts);
    const bars = generateStageBarChart(data, max, categories);
    return (<C3Chart data={bars.data} bar={bars.bar} axis={bars.axis}/>);
  }

  renderActivityItem = (activity) => {
    return ([<ListItem
      leftAvatar={<Avatar src="/images/avatar2.png"/>}
      primaryText={activity.by ? activity.by.name : ''}
      secondaryText={<div>
        <div>{getAction(activity)}</div>
        <div>{moment(activity.at).format('lll')}</div>
      </div>}
      secondaryTextLines={2}
    />,
      <Divider/>]);
  };

  renderNoActivity = () => {
    return this.renderNoData('No Activity');
  };

  renderReport = () => {
    const { job, activity } = this.props;
    const { jobProgressData, cumulative, jobProgressTimeSeriesData } = this.state;

    const stages = (job.profile || {}).stages || [];
    console.log('stages', stages);
    const stage = stages[this.state.selectedStageIndex];

    const jobProgressTimeSeriesChart = cumulative ? jobProgressTimeSeriesData.cumulative : jobProgressTimeSeriesData.absolute;

    return (<div>
      <div style={styles.summaryCharts}>
        <Card style={styles.card}>
          <CardTitle title="Overall Progress" titleColor="white" style={styles.blueCardTitle}/>
          <CardText>
            { jobProgressData ? <C3Chart data={jobProgressData.data} color={jobProgressData.color}
                                         size={{ height: 320 }}/> : 'Loading...' }
          </CardText>
        </Card>
        <Card style={styles.card}>
          <CardTitle
            title={<div style={styles.title}><span style={{ display: 'inline-block' }}>Stage Progress</span>
              <span style={{ display: 'flex', flexDirection: 'row' }}>
                  <RaisedButton label="Percentage" onClick={() => { this.setState({ gauge: true }); }}
                                labelColor="#FFFFFF" backgroundColor={this.state.gauge ? '#003F87' : '#3579DC'}/>
                  <RaisedButton label="Absolute" onClick={() => { this.setState({ gauge: false }); }}
                                labelColor="#FFFFFF" backgroundColor={!this.state.gauge ? '#003F87' : '#3579DC'}/>
                </span></div>} titleColor="white" style={styles.blueCardTitle}
          />
          <CardText>
            {this.state.gauge ? <div>{this.renderStageProgressChart(stage)}
              <BottomNavigation selectedIndex={this.state.selectedStageIndex}>
                { stages.map(this.renderStageNavigationItem)}
              </BottomNavigation></div> : this.renderStageBarChart(stage)}
          </CardText>
        </Card>
      </div>
      <div style={styles.stats}>
        <Card style={styles.card2}>
          <CardTitle
            title={<div style={styles.title}>
              <span style={{ display: 'inline-block' }}>Job Progress</span>
              <span style={{ display: 'flex', flexDirection: 'row' }}>
                <RaisedButton
                  label="Cumulative"
                  onClick={() => { this.setState({ cumulative: true }); }}
                  labelColor="#FFFFFF"
                  backgroundColor={!this.state.cumulative ? '#003F87' : '#3579DC'}/>
                <RaisedButton
                  label="Absolute"
                  onClick={() => { this.setState({ cumulative: false }); }}
                  labelColor="#FFFFFF"
                  backgroundColor={this.state.cumulative ? '#003F87' : '#3579DC'}/>
              </span>
            </div>} titleColor="white" style={styles.blueCardTitle}/>
          <CardText>
            { jobProgressTimeSeriesChart ? <C3Chart
              data={jobProgressTimeSeriesChart.data}
              axis={jobProgressTimeSeriesChart.axis}
            /> : 'Loading...' }
          </CardText>
        </Card>
        {/*<div style={styles.card}>*/}
        {/*<Card>*/}
        {/*<CardTitle title={<div style={styles.title}><span style={{ display: 'inline-block' }}>Performance</span>*/}
        {/*<span style={{ display: 'flex', flexDirection: 'row' }}>*/}
        {/*<RaisedButton label="Best" onClick={() => { this.setState({ best: true }); }} labelColor="#FFFFFF"*/}
        {/*backgroundColor={this.state.gauge ? '#003F87' : '#3579DC'}/>*/}
        {/*<RaisedButton label="Worst" onClick={() => { this.setState({ best: false }); }}*/}
        {/*labelColor="#FFFFFF" backgroundColor={!this.state.gauge ? '#003F87' : '#3579DC'}/>*/}
        {/*</span></div>} titleColor="white" style={styles.greenCardTitle}/>*/}
        {/*<CardText>*/}
        {/*<List>*/}
        {/*<ListItem*/}
        {/*leftAvatar={<Avatar src={require('../../../../images/avatar1.png')}/>}*/}
        {/*primaryText="Ava Thornton"*/}
        {/*secondaryText={<div>*/}
        {/*<div>Pickup at US-MTV-1215, Desk 185</div>*/}
        {/*<div>{moment().format('lll')}</div>*/}
        {/*</div>}*/}
        {/*secondaryTextLines={2}*/}
        {/*/>*/}
        {/*<Divider inset/>*/}
        {/*<ListItem*/}
        {/*leftAvatar={<Avatar src={require('../../../../images/avatar2.png')}/>}*/}
        {/*primaryText="Bob Wilson"*/}
        {/*secondaryText={<div>*/}
        {/*<div>Inventorize at US-MTV-2398, Desk 281</div>*/}
        {/*<div>{moment().format('lll')}</div>*/}
        {/*</div>}*/}
        {/*secondaryTextLines={2}*/}
        {/*/>*/}
        {/*<Divider inset/>*/}
        {/*<ListItem*/}
        {/*leftAvatar={<Avatar src={require('../../../../images/avatar3.jpg')}/>}*/}
        {/*primaryText="Richard Knight"*/}
        {/*secondaryText={<div>*/}
        {/*<div>Dropoff at US-MTV-639, Desk 836</div>*/}
        {/*<div>{moment().format('lll')}</div>*/}
        {/*</div>}*/}
        {/*secondaryTextLines={2}*/}
        {/*/>*/}
        {/*</List>*/}
        {/*</CardText>*/}
        {/*</Card>*/}
        {/*</div>*/}
        <Card style={styles.card}>
          <CardTitle title="Recent Activity" titleColor="white" style={styles.blueCardTitle}/>
          <CardText>
            <List>
              { (activity || []).length > 0 ? (activity || []).map(this.renderActivityItem) : this.renderNoActivity() }
              {/*<Divider inset/>*/}
            </List>
          </CardText>
        </Card>
      </div>
    </div>);
  }

  renderNoData() {
    return (<div style={styles.noDataRow}>
      <FontIcon className="material-icons" style={styles.noData}>transform</FontIcon>
      <div>No tasks are available</div>
    </div>);
  }

  render() {
    const { loading, job, tasks } = this.props;
    const { processing } = this.state;
    //
    // if (!loading && !processing && tasks && tasks.length === 0) {
    //   return
    // }

    return (
      <Dashboard
        title={`Job Report - ${job.customer ? job.customer.name : ''}${job.description ? ` - ${job.description}` : ''}`}>
        { !loading && !processing ? (tasks.length > 0 ? this.renderReport() : this.renderNoData()) : <Loading/> }
      </Dashboard>
    );
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchJob,
    fetchTasks: (jobId) => fetchTasksRoutine.trigger({ jobId }),
    fetchEmployees,
    fetchRecentActivity,
  }, dispatch);
}

function mapStateToProps(state) {
  const job = state.get('jobs').get('job').toJS();
  // const hasTasks = job && job.tasks && job.tasks.count;
  console.log('l1', state.get('jobs').get('loading'));
  console.log('l2', state.get('tasks').get('loading'));
  return {
    job,
    tasks: toArray(state.get('tasks').get('tasks').toJS()), // TODO remove use job stats instead
    loading: state.get('jobs').get('loading') || (state.get('tasks').get('loading')),
    dialogs: state.get('dialogs').get('dialogs').toJS(),
    employees: state.get('employees').get('employees').toJS(),
    activity: toArray(state.get('activity').get('recent').toJS()).sort((a, b) => sortStrings(b.at, a.at)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(JobReport);



// WEBPACK FOOTER //
// ./src/components/Reports/JobReport/index.js